import { useState } from "react";
import { useNavigate, Link, Navigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import api, { errorMessage } from "../lib/api";
import { AtSign, KeyRound, Building2, User } from "lucide-react";
import { BrandLogo } from "../components/BrandLogo";
import { toast } from "sonner";

export default function Signup() {
  const { user, loading, setUser } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({ agency_name: "", full_name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);

  if (loading) return null;
  if (user) return <Navigate to="/" replace />;

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (form.password.length < 8) return toast.error("Password must be 8+ characters");
    setBusy(true);
    try {
      const r = await api.post("/agencies/signup", form);
      setUser(r.data.user);
      toast.success(`Welcome — ${r.data.agency.name} is now live`);
      nav("/", { replace: true });
    } catch (e) {
      toast.error(errorMessage(e, "Signup failed"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0A0A0A] text-zinc-100 p-6">
      <div className="w-full max-w-md border border-[#27272A] bg-[#0E0E0E] p-10">
        <div className="flex items-center gap-3 mb-8">
          <BrandLogo variant="icon" height={44} />
          <div>
            <div className="font-display font-black text-xl uppercase tracking-tighter">Iris Security</div>
            <div className="label-mute">Start your agency</div>
          </div>
        </div>

        <h2 className="font-display text-3xl font-black uppercase tracking-tighter mb-1">Create Agency</h2>
        <p className="text-zinc-400 text-sm mb-6">Free tier — up to 10 , 5 sites, 5 users.</p>

        <form onSubmit={submit} className="space-y-4" data-testid="signup-form">
          <Field icon={Building2} placeholder="Agency name" value={form.agency_name} onChange={(v) => set("agency_name", v)} testid="signup-agency-name" />
          <Field icon={User} placeholder="Your full name" value={form.full_name} onChange={(v) => set("full_name", v)} testid="signup-full-name" />
          <Field icon={AtSign} placeholder="you@agency.com" type="email" value={form.email} onChange={(v) => set("email", v)} testid="signup-email" />
          <Field icon={KeyRound} placeholder="Password (8+ chars)" type="password" value={form.password} onChange={(v) => set("password", v)} testid="signup-password" />

          <button
            type="submit"
            disabled={busy}
            data-testid="signup-submit-button"
            className="w-full bg-[#FF3B30] hover:bg-[#FF453A] text-white font-display uppercase tracking-widest text-sm py-3 transition-colors duration-150 disabled:opacity-50"
          >
            {busy ? "Creating…" : "Create agency"}
          </button>
        </form>

        <div className="mt-6 text-center text-xs text-zinc-500">
          Already have an account?{" "}
          <Link to="/login" className="text-zinc-300 hover:text-[#FF3B30] underline" data-testid="signup-to-login-link">
            Sign in
          </Link>
        </div>
      </div>
    </div>
  );
}

function Field({ icon: Icon, placeholder, value, onChange, type = "text", testid }) {
  return (
    <div className="relative">
      <Icon className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-zinc-500" />
      <input
        data-testid={testid}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        required
        className="w-full bg-[#0A0A0A] border border-[#27272A] focus:border-[#FF3B30] outline-none pl-10 pr-3 py-3 text-sm text-zinc-100 placeholder:text-zinc-600"
      />
    </div>
  );
}
